import type { VisibleContract } from "@contract-manager/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { Alert } from "../components/Alert";
import { StatusBadge } from "../components/StatusBadge";
import { UploadPanel } from "../components/UploadPanel";
import { useI18n } from "../i18n/I18n";
import type { MessageKey } from "../i18n/messages";
import { hasPendingWork, POLL_INTERVAL_MS } from "../polling";
import {
  CONTRACT_TYPE_LABEL,
  ENTITY_LABEL,
  readTrackerLabel,
} from "../tracker";

/** The tracker's columns, in its order, minus what only the detail shows. */
const COLUMNS: readonly MessageKey[] = [
  "contracts.list.column.fileName",
  "contracts.list.column.type",
  "contracts.list.column.counterparty",
  "contracts.list.column.entity",
  "contracts.list.column.endDate",
  "contracts.list.column.status",
  "contracts.list.column.uploadedAt",
];

export function ContractListPage({ api }: { api: ApiClient }) {
  const auth = useAuth();
  const { t, count, formatTimestamp } = useI18n();
  const [items, setItems] = useState<VisibleContract[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  // The failure itself, translated at render: switching language should not
  // leave the message behind in the old one, nor refetch the list.
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);
  /** True only for the very first load, which is what shows the skeleton. */
  const [ready, setReady] = useState(false);

  const load = useCallback(
    async (from?: string, options: { quiet?: boolean } = {}) => {
      if (options.quiet !== true) {
        setLoading(true);
      }
      setFailure(null);
      try {
        const page = await api.listContracts(
          from === undefined ? {} : { cursor: from },
        );
        // `from === undefined` is a reload, not a next page: replace rather
        // than append, or an upload would duplicate every visible row.
        setItems((current) =>
          from === undefined ? page.items : [...current, ...page.items],
        );
        setCursor(page.nextCursor);
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return;
        }
        setFailure({ cause: caught });
      } finally {
        setLoading(false);
        setReady(true);
      }
    },
    [api, auth],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Keep refreshing while anything is still being extracted.
   *
   * Only the first page is re-fetched, and quietly — a spinner blinking every
   * five seconds over a table someone is reading is worse than no spinner.
   * Any pages loaded with "もっと読む" are dropped by the reload, which is the
   * same thing the upload path already does.
   */
  const waiting = hasPendingWork(items);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!waiting) {
      return;
    }
    const timer = setInterval(() => {
      void loadRef.current(undefined, { quiet: true });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [waiting]);

  return (
    <div className="ds-rows --gap-medium">
      <div className="ds-cols --baseline --gap-small">
        <h1>{t("contracts.list.title")}</h1>
        {ready && (
          <span className="ds-text --small --muted">
            {count("contracts.list.count", items.length)}
          </span>
        )}
        {waiting && (
          <span className="ds-tag --small --info --pulse app-push-end">
            <span>{t("contracts.list.extracting")}</span>
          </span>
        )}
      </div>

      <UploadPanel api={api} onUploaded={() => void load()} />

      {failure !== null && (
        <Alert tone="failed" title={t("contracts.list.loadFailed")}>
          {describeError(failure.cause, t)}
        </Alert>
      )}

      {!ready ? (
        <SkeletonTable />
      ) : items.length === 0 ? (
        <div className="ds-notice --light --center">
          <h3>{t("contracts.list.emptyTitle")}</h3>
          <p>{t("contracts.list.emptyBody")}</p>
        </div>
      ) : (
        <div className="app-scroll-x">
          <table className="ds-datatable">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column}>{t(column)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((contract) => (
                <tr key={contract.contractId}>
                  <td>
                    <Link
                      to={`/contracts/${contract.contractId}`}
                      className="ds-text --medium-bold app-truncate app-file-name"
                      title={contract.originalFileName}
                    >
                      {contract.originalFileName}
                    </Link>
                    {contract.extracted?.title !== undefined && (
                      <div className="ds-text --small --muted">
                        {contract.extracted.title}
                      </div>
                    )}
                  </td>
                  <td>
                    {contract.extracted === undefined ? (
                      <Unknown />
                    ) : (
                      readTrackerLabel(
                        CONTRACT_TYPE_LABEL[contract.extracted.contractType],
                        t,
                      )
                    )}
                  </td>
                  <td title={contract.extracted?.counterparty}>
                    {contract.extracted === undefined ? (
                      <Unknown />
                    ) : (
                      // The short name is what the tracker files under; the
                      // full name is a hover away.
                      (contract.extracted.counterpartyShortName ??
                      contract.extracted.counterparty)
                    )}
                  </td>
                  <td>
                    {contract.extracted === undefined ? (
                      <Unknown />
                    ) : (
                      readTrackerLabel(
                        ENTITY_LABEL[contract.extracted.contractingEntity],
                        t,
                      )
                    )}
                  </td>
                  <td className="app-mono">
                    {contract.extracted?.endDate ?? <Unknown />}
                  </td>
                  <td>
                    <StatusBadge status={contract.ingestStatus} />
                  </td>
                  <td className="app-mono">
                    {formatTimestamp(contract.uploadedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cursor !== undefined && (
        <div className="ds-cols --center">
          <button
            type="button"
            className={`ds-button${loading ? " --loading" : ""}`}
            disabled={loading}
            // SDS hides the label of a loading button; keep its name.
            aria-label={loading ? t("contracts.loadingEllipsis") : undefined}
            onClick={() => void load(cursor)}
          >
            <span>
              {loading ? t("contracts.loadingEllipsis") : t("common.loadMore")}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function Unknown() {
  return <span className="ds-text --muted">—</span>;
}

/**
 * The header row over an SDS loading state, so the first load does not flash
 * an empty state. SDS hides the contents of a loading table, so the busy state
 * is also given to assistive technology directly.
 */
function SkeletonTable() {
  const { t } = useI18n();
  return (
    <table
      className="ds-datatable --loading"
      aria-busy="true"
      aria-label={t("common.loading")}
    >
      <thead>
        <tr>
          {COLUMNS.map((column) => (
            <th key={column}>{t(column)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {COLUMNS.map((column) => (
            <td key={column}>&nbsp;</td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}
